import React, { useEffect, useState } from "react";
import OrderLinesTable from "../../../../components/Orders/OrderLinesTable";
import {
  Spinner,
  Alert,
  Modal,
} from "react-bootstrap";
import { withStyles } from "@material-ui/core/styles";
import { FormControlLabel, Switch } from "@material-ui/core";
import { useParams, useHistory } from "react-router-dom";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { io } from "socket.io-client";
import {
  MdCancel,
  MdLocalPrintshop,
  MdSportsMotorsports,
  MdArrowBackIosNew,
  MdOutlineCreditCardOff,
  MdOutlineCreditCard,
  MdLocalShipping,
  MdUndo,
} from "react-icons/md";
import {
  assignRestaurantOrderToDeliveryAgent,
  getOrderDetailsToProcess,
  markOrderAsPaid,
  updateRestaurantOrderDeliveryStatus,
} from "../../../../actions/webOrderActions";
import { toast } from "react-toastify";
import axios from "axios";
import { RECEIPT_URL } from "../../../../api/receiptUrl";
import { RESET_UPDATE_DELIVERY_STATUS_COMPLETE } from "../../../../constants/webOrderConstants";
import "./RestaurantOrderDetails.css";
import { getUserFromLocalStorage } from "utilities/user";
import ThreePLPartnerDropdown from "../../../../components/ThreePLPartnerDropdown";
import RiderSelectionDropdown from "./RiderSelectionDropdown";
import { getDeliveryLabel } from "../../../../utilities/paymentStatus";
import CancelOrderFields, { useCancellationReason, CancellationRecord } from "../../../../components/Orders/CancelOrderFields";
import ContactAction from "../../../../components/Orders/ContactAction";
import DropoffMap from "../../../../components/Maps/DropoffMap";
import VendorHandoffActions from "../../../../components/Orders/VendorHandoffActions";
import { getShippingAddressText } from "../../../../utilities/orderPresentation";
import { openReceiptForPrint } from "../../../../utilities/openReceipt";
import {
  getDeliveryProgress,
  canVendorDispatch,
  canVendorMarkDelivered,
  AUDIENCE,
} from "../../../../utilities/orderDeliverySteps";
import OrderNextStep from "../../../../components/Orders/OrderNextStep";
// An ordered dish may have no photograph yet (menus are imported before
// the shoot), so these thumbnails need a fallback rather than a broken box.
// Fork, plate and knife on a plain near-white field: the restaurant
// placeholder for a dish that has not been photographed yet.
import menuPlaceholder from "assets/img/restaurant-dish-placeholder.png";
import socketAuthOptions from "../../../../utilities/socketAuth";
import useOrderLiveUpdates from "../../../../hooks/useOrderLiveUpdates";
import AppSelect from "../../../../components/ui/AppSelect";

const API_URL = process.env.REACT_APP_SERVER_URL;

const IOSSwitch = withStyles((theme) => ({
  root: {
    width: 42,
    height: 26,
    padding: 0,
    margin: theme.spacing(1),
  },
  switchBase: {
    padding: 1,
    "&$checked": {
      transform: "translateX(16px)",
      color: theme.palette.common.white,
      "& + $track": {
        backgroundColor: "#52d869",
        opacity: 1,
        border: "none",
      },
    },
    "&$focusVisible $thumb": {
      color: "#52d869",
      border: "6px solid #fff",
    },
  },
  thumb: {
    width: 24,
    height: 24,
  },
  track: {
    borderRadius: 26 / 2,
    border: `1px solid ${theme.palette.grey[400]}`,
    backgroundColor: theme.palette.grey[50],
    opacity: 1,
    transition: theme.transitions.create(["background-color", "border"]),
  },
  checked: {},
  focusVisible: {},
}))(({ classes, ...props }) => {
  return (
    <Switch
      focusVisibleClassName={classes.focusVisible}
      disableRipple
      classes={{
        root: classes.root,
        switchBase: classes.switchBase,
        thumb: classes.thumb,
        track: classes.track,
        checked: classes.checked,
      }}
      {...props}
    />
  );
});

const isDevelopment = process.env.NODE_ENV === "development";

const RestaurantOrderDetails = () => {
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const user = getUserFromLocalStorage();

  const [showCancelModal, setShowCancelModal] = useState(false);
  // Reason + note for the audit record. Required by the server on cancel.
  const cancelReason = useCancellationReason();
  const [riders, setRiders] = useState([]);
  const [riderId, setRiderId] = useState("");
  // const [receiptLoading, setReceiptLoading] = useState(false);
  const [loadingRiders, setLoadingRiders] = useState(true);
  const [elapsedAssignedTime, setElapsedAssignedTime] = useState(0);
  const [elapsedDispatchedTime, setElapsedDispatchedTime] = useState(0);

  // 3PL Partner assignment state
  const [assignmentMode, setAssignmentMode] = useState("rider"); // "rider" or "3pl"
  const [selectedPartnerId, setSelectedPartnerId] = useState("");
  const [assigning3PL, setAssigning3PL] = useState(false);
  const [recalling3PL, setRecalling3PL] = useState(false);
  const [showRecallModal, setShowRecallModal] = useState(false);

  const mobileOrderDeliver = useSelector((state) => state.mobileOrderDeliver);
  // const { loading: loadingDeliver, success: successDeliver } = mobileOrderDeliver;

  const orderDetails = useSelector((state) => state.orderDetails);
  const {
    order,
    loading,
    error,
    isUpdateDeliveryStatusComplete,
    paymentResult,
  } = orderDetails;

  // Live order news (a rider's pickup, a 3PL delivery, a decline) through the
  // one shared feed; see hooks/useOrderLiveUpdates.js.
  useOrderLiveUpdates(id, {
    onChange: (fresh) => {
      if (fresh?.delivery_status === "PROCESSING" && !fresh?.riderId) setRiderId("");
    },
  });

  useEffect(() => {
    dispatch(getOrderDetailsToProcess(id));
    if (isUpdateDeliveryStatusComplete) {
      dispatch(getOrderDetailsToProcess(id));
      dispatch({ type: RESET_UPDATE_DELIVERY_STATUS_COMPLETE });
    }
  }, [isUpdateDeliveryStatusComplete, id, dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const socketUrl = process.env.REACT_APP_SERVER_URL;

  useEffect(() => {
    const socket = io(
      socketUrl,
      socketAuthOptions({ userId: user?.result?._id })
    );

    socket.on("connect", () => {
      if (isDevelopment) {
        console.log("Webclient Connected");
      }
      socket.emit("getRiders");
    });

    socket.on("lusukuRiders", (socketData) => {
      setRiders(socketData);
      setLoadingRiders(false);
    });

    return () => {
      socket.disconnect();
    };
  }, [user?.result?._id]);

  const handleBack = () => {
    // Navigate back to appropriate orders page based on user role
    const isSuperAdmin =
      user?.result?.user_role === "SUPERUSER" ||
      user?.result?.userType === "LUSUKU STAFF";

    if (isSuperAdmin) {
      history.push("/admin/paginatedOrdersTable");
    } else {
      history.push("/admin/resOrdersTable");
    }
  };

  const handleUpdateStatus = (newStatus) => {
    if (newStatus === "ASSIGNED" && !riderId) {
      toast.warn("Please select a rider before assigning the order.");
      return;
    }
    if (newStatus === "ASSIGNED") {
      assignRestaurantOrderToRider();
    } else {
      dispatch(updateRestaurantOrderDeliveryStatus(order?._id, newStatus));
    }
  };

  const handleCancelOrder = () => {
    // Refuse locally too, so the modal explains itself instead of
    // bouncing off a 400 from the server.
    if (!cancelReason.validate()) return;
    const delivery_status = "REJECTED";
    
    dispatch(updateRestaurantOrderDeliveryStatus(order?._id, delivery_status, cancelReason.payload()));
    setShowCancelModal(false);
    cancelReason.reset();
  };

  // The receipt is its own document (the fiscal receipt with the EFRIS
  // markings when issued), not a print of this page.
  const handlePrint = () => openReceiptForPrint(order?._id);

  const handleMarkAsPaid = () => {
    if (user?.result?.userType !== "LUSUKU STAFF" && user?.result?.user_role !== "SUPERUSER") {
      toast.warn("🚫 You are not authorized to mark order as paid!");
      return;
    } else {
      dispatch(markOrderAsPaid(order?._id));
    }
  };

  const assignRestaurantOrderToRider = () => {
    const delivery_status = "ASSIGNED";
    const assignedTime = Date.now();
    // console.log(`Assigning order ${order?._id} to rider ${riderId}`);
    toast.success(`Assigning order ${order?._id} to rider ${riderId}`);
    localStorage.setItem(`order_${order?._id}_assignedTime`, assignedTime);
    dispatch(
      assignRestaurantOrderToDeliveryAgent(order?._id, riderId, delivery_status)
    );
    setTimeout(() => {
      dispatch(getOrderDetailsToProcess(order?._id));
    }, 500);
  };

  // Assign order to 3PL Partner
  const assignOrderTo3PLPartner = async () => {
    if (!selectedPartnerId) {
      toast.warn("Please select a 3PL partner");
      return;
    }

    setAssigning3PL(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/weborders/${order?._id}/assign-to-3pl`,
        { partnerId: selectedPartnerId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        toast.success(response.data.message || "Order assigned to 3PL partner!");
        setTimeout(() => {
          dispatch(getOrderDetailsToProcess(order?._id));
        }, 500);
      } else {
        toast.error(response.data.error?.message || "Failed to assign order");
      }
    } catch (error) {
      console.error("Error assigning to 3PL:", error);
      toast.error(
        error.response?.data?.error?.message || "Failed to assign order to 3PL partner"
      );
    } finally {
      setAssigning3PL(false);
    }
  };

  // Recall order from 3PL Partner
  const recallOrderFrom3PL = async () => {
    setShowRecallModal(false);
    setRecalling3PL(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/weborders/${order?._id}/recall-from-3pl`,
        { reason: "Recalled by vendor" },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(response.data.message || "Order recalled successfully!");
        dispatch(getOrderDetailsToProcess(order?._id));
      } else {
        toast.error(response.data.error?.message || "Failed to recall order");
      }
    } catch (error) {
      console.error("Error recalling from 3PL:", error);
      toast.error(
        error.response?.data?.error?.message || "Failed to recall order from 3PL partner"
      );
    } finally {
      setRecalling3PL(false);
    }
  };

  const handleDispatchRestaurantOrder = () => {
    const delivery_status = "DISPATCHED";
    const dispatchedTime = Date.now();
    console.log(`Dispatching order ${order?._id}`);
    localStorage.setItem(`order_${order?._id}_dispatchedTime`, dispatchedTime);
    dispatch(updateRestaurantOrderDeliveryStatus(order?._id, delivery_status));
    setTimeout(() => {
      dispatch(getOrderDetailsToProcess(order?._id));
    }, 500);
  };

  const handleDeliverRestaurantOrder = () => {
    const delivery_status = "DELIVERED";
    dispatch(updateRestaurantOrderDeliveryStatus(order?._id, delivery_status));
    setTimeout(() => {
      dispatch(getOrderDetailsToProcess(order?._id));
    }, 500);
  };

  // The delivery ladder lives in utilities/orderDeliverySteps.js. This was a
  // four-case switch whose `default: 0` emptied the bar for the six statuses
  // this page never writes itself: ACCEPTED and PICKED_UP come from the rider
  // app, PENDING_3PL / IN_TRANSIT / ARRIVED from the 3PL rail, FULFILLED from
  // the counter. A rider tapping Accept made the order look like it had gone
  // backwards to zero.
  //
  // This is an ADMIN surface, so it takes the progress and the button rules but
  // NOT the vendor-facing OrderNextStep banner, whose copy ("Your turn", "press
  // Dispatch Order") is addressed to the vendor working the order.
  //
  // NOTE: there is no "PAID" delivery_status. Payment state lives in
  // `isPaid`/`paymentStatus` (see utilities/paymentStatus.js); the
  // delivery_status enum in models/Orders.js has no such member.
  const getProgress = () => getDeliveryProgress(order);

  const getProgressBarStyle = () => {
    switch (order?.delivery_status) {
      case "DELIVERED":
        return { backgroundColor: "green", color: "white" };
      default:
        return {};
    }
  };

  const getBadgeStyle = (status) => {
    switch (status) {
      case "PROCESSING":
        return {
          backgroundColor: "orange",
          color: "white",
          borderRadius: "10px",
          padding: "5px",
          width: "110px",
          height: "20px",
          justifyContent: "center",
          alignItems: "center",
          display: "flex",
        };
      case "ASSIGNED":
        return {
          backgroundColor: "blue",
          color: "white",
          borderRadius: "10px",
          padding: "5px",
        };
      case "DISPATCHED":
        return {
          backgroundColor: "purple",
          color: "white",
          borderRadius: "10px",
          padding: "5px",
        };
      case "DELIVERED":
        return {
          backgroundColor: "green",
          color: "white",
          borderRadius: "10px",
          padding: "5px",
        };
      case "REJECTED":
        return {
          backgroundColor: "red",
          color: "white",
          borderRadius: "10px",
          padding: "5px",
        };
      default:
        return {
          backgroundColor: "grey",
          color: "white",
          borderRadius: "10px",
          padding: "5px",
        };
    }
  };

  const getPaidBadgeStyle = (isPaid) => {
    return {
      backgroundColor: isPaid ? "green" : "#2C3A40",
      color: "white",
      borderRadius: "10px",
      padding: "3px",
    };
  };

  // Only an order still in PROCESSING can be cancelled here. This used to also
  // accept a "PAID" delivery_status, which is not a member of the delivery_status
  // enum and could never be stored — dead code that made the rule look broader
  // than it is. Behaviour is unchanged.
  const canCancelOrder = order?.delivery_status === "PROCESSING";

  const getDisabledStatusOptions = () => {
    const disabledOptions = {
      PROCESSING: order?.delivery_status !== "PROCESSING",
      ASSIGNED:
        order?.delivery_status !== "PROCESSING" &&
        !canVendorDispatch(order),
      DISPATCHED:
        !canVendorDispatch(order) &&
        !canVendorMarkDelivered(order),
      DELIVERED:
        !canVendorMarkDelivered(order) &&
        order?.delivery_status !== "DELIVERED",
      REJECTED:
        order?.delivery_status === "DELIVERED" ||
        order?.delivery_status === "REJECTED",
    };
    return disabledOptions;
  };

  const disabledStatusOptions = getDisabledStatusOptions();

  useEffect(() => {
    if (order?.delivery_status === "ASSIGNED") {
      const storedAssignedTime = localStorage.getItem(
        `order_${order?._id}_assignedTime`
      );
      if (storedAssignedTime) {
        const elapsed = Math.floor(
          (Date.now() - parseInt(storedAssignedTime, 10)) / 1000
        );
        setElapsedAssignedTime(elapsed);
      }
    }
    if (order?.delivery_status === "DISPATCHED") {
      const storedDispatchedTime = localStorage.getItem(
        `order_${order?._id}_dispatchedTime`
      );
      if (storedDispatchedTime) {
        const elapsed = Math.floor(
          (Date.now() - parseInt(storedDispatchedTime, 10)) / 1000
        );
        setElapsedDispatchedTime(elapsed);
      }
    }

    const interval = setInterval(() => {
      if (order?.delivery_status === "ASSIGNED") {
        setElapsedAssignedTime((prev) => prev + 1);
      }
      if (order?.delivery_status === "DISPATCHED") {
        setElapsedDispatchedTime((prev) => prev + 1);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [order?.delivery_status, order?._id]);

  if (loading) {
    return (
      <Spinner
        animation="grow"
        variant="primary"
        style={{
          marginTop: "70px",
          top: "50%",
          left: "50%",
          position: "fixed",
        }}
      />
    );
  }

  if (error) {
    return (
      <Alert variant="danger" style={{ marginTop: "70px" }}>
        {error}
      </Alert>
    );
  }

  const statusSteps = ["PROCESSING", "ASSIGNED", "DISPATCHED", "DELIVERED"];
  const currentStepIdx = statusSteps.indexOf(order?.delivery_status);
  const getStepClass = (idx) => {
    if (idx < currentStepIdx) return "done";
    if (idx === currentStepIdx) return "active";
    return "";
  };
  const statusChipClass = {
    PROCESSING: "rod-chip-processing",
    ASSIGNED:   "rod-chip-assigned",
    DISPATCHED: "rod-chip-dispatched",
    DELIVERED:  "rod-chip-delivered",
    REJECTED:   "rod-chip-rejected",
    PENDING_3PL:"rod-chip-pending3pl",
  }[order?.delivery_status] || "rod-chip-processing";

  return (
    <div className="rod-canvas">

      {/* ── Top Bar ── */}
      <div className="rod-topbar">
        <div className="rod-topbar-title">
          <h4>Menu Order Details</h4>
          <span>Order #{order?._id}</span>
          {order?.createdAt && (
            <span className="rod-topbar-date">
              {moment(order.createdAt).format("MMM D, YYYY · h:mm a")}
            </span>
          )}
        </div>
        <div className="rod-topbar-actions">
          <button className="rod-btn rod-btn-ghost" onClick={handleBack}>
            <MdArrowBackIosNew style={{ fontSize: 13 }} /> Back
          </button>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <VendorHandoffActions orderId={order?._id} />
            <button className="rod-btn rod-btn-ghost" onClick={handlePrint}>
              <MdLocalPrintshop style={{ fontSize: 14 }} /> Print Receipt
            </button>
          </div>
          <button
            className="rod-btn rod-btn-danger"
            onClick={() => setShowCancelModal(true)}
            disabled={!canCancelOrder}
          >
            <MdCancel style={{ fontSize: 14 }} /> Cancel Order
          </button>
          {/* components/Orders/ContactAction: a real tel: link on a phone,
              copy on a desktop. The old button only raised a toast. */}
          <ContactAction
            label="Customer"
            name={order?.customerName}
            phone={order?.shippingAddress?.customerNumber || order?.shippingAddress?.recipientPhone}
            style={{ marginRight: 5 }}
          />
        </div>
      </div>

      {/* ── KPI Tiles ── */}
      <div className="rod-kpi-row">
        <div className="rod-kpi-tile orange">
          <div className="rod-kpi-label">Items Total</div>
          <div className="rod-kpi-value">Ush {order?.itemsPrice?.toLocaleString()}</div>
          <div className="rod-kpi-sub">{order?.orderItems?.length || 0} item{order?.orderItems?.length !== 1 ? "s" : ""}</div>
        </div>
        <div className="rod-kpi-tile blue">
          <div className="rod-kpi-label">Total Price</div>
          <div className="rod-kpi-value">Ush {order?.totalPrice?.toLocaleString()}</div>
          <div className="rod-kpi-sub">Tax: Ush {order?.taxPrice?.toLocaleString()}</div>
        </div>
        <div className={`rod-kpi-tile ${order?.isPaid ? "green" : "red"}`}>
          <div className="rod-kpi-label">Payment</div>
          <div className="rod-kpi-value" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {order?.isPaid
              ? <><MdOutlineCreditCard style={{ fontSize: 18 }} /> Paid</>
              : <><MdOutlineCreditCardOff style={{ fontSize: 18 }} /> Unpaid</>
            }
          </div>
          <div className="rod-kpi-sub">{order?.paymentMethod}</div>
        </div>
        <div className="rod-kpi-tile">
          <div className="rod-kpi-label">Order Status</div>
          <div className="rod-kpi-value" style={{ fontSize: "0.95rem", marginTop: 4 }}>
            <span className={`rod-chip ${statusChipClass}`}>{getDeliveryLabel(order?.delivery_status)}</span>
          </div>
          <div className="rod-kpi-sub">{moment(order?.createdAt).format("MMM D, YYYY · h:mm a")}</div>
        </div>
      </div>

      {/* ── Step Tracker ── */}
      <div className="rod-progress-track">
        {statusSteps.map((step, idx) => (
          <div key={step} className={`rod-step ${getStepClass(idx)}`}>
            <div className="rod-step-dot">{idx + 1}</div>
            <div className="rod-step-label">{step}</div>
          </div>
        ))}
      </div>

      {/* ── Row 1: Order Items + Order Info ── */}
      <div className="rod-main-grid" style={{ marginBottom: 14 }}>

        {/* Order Items Table */}
        <div className="rod-panel">
          <div className="rod-panel-header">
            <div className="rod-panel-accent" />
            <span className="rod-panel-title">Order Items</span>
          </div>
          <div className="rod-panel-body" style={{ padding: 0 }}>
            <div className="rod-table-wrap">
              <OrderLinesTable order={order} placeholderImage={menuPlaceholder} />
            </div>
          </div>
        </div>

        {/* Right column: Summary + Address stacked */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

          {/* Order Summary */}
          <div className="rod-panel">
            <div className="rod-panel-header">
              <div className="rod-panel-accent" style={{ background: "#60a5fa" }} />
              <span className="rod-panel-title">Order Summary</span>
            </div>
            <div className="rod-panel-body">
              <div className="rod-info-row">
                <span className="rod-info-label">Customer</span>
                <span className="rod-info-value">{order?.customerName}</span>
              </div>
              <div className="rod-info-row">
                <span className="rod-info-label">Payment Method</span>
                <span className="rod-info-value">{order?.paymentMethod}</span>
              </div>
              <div className="rod-info-row">
                <span className="rod-info-label">Items Price</span>
                <span className="rod-info-value">Ush {order?.itemsPrice?.toLocaleString()}</span>
              </div>
              <div className="rod-info-row">
                <span className="rod-info-label">Tax</span>
                <span className="rod-info-value">Ush {order?.taxPrice?.toLocaleString()}</span>
              </div>
              <div className="rod-info-row">
                <span className="rod-info-label">Total</span>
                <span className="rod-info-value" style={{ color: "#f2c811", fontWeight: 700 }}>
                  Ush {order?.totalPrice?.toLocaleString()}
                </span>
              </div>
              <div className="rod-info-row">
                <span className="rod-info-label">Payment Status</span>
                <span className={`rod-chip ${order?.isPaid ? "rod-chip-paid" : "rod-chip-unpaid"}`}>
                  {order?.isPaid
                    ? <><MdOutlineCreditCard style={{ fontSize: 11 }} /> Paid</>
                    : <><MdOutlineCreditCardOff style={{ fontSize: 11 }} /> Unpaid</>
                  }
                </span>
              </div>
              <div className="rod-info-row">
                <span className="rod-info-label">Order Date</span>
                <span className="rod-info-value" style={{ fontSize: "0.75rem" }}>
                  {moment(order?.createdAt).format("MMM D YYYY, h:mm a")}
                </span>
              </div>
            </div>
          </div>

          {/* Shipping Address */}
          <div className="rod-panel">
            <div className="rod-panel-header">
              <div className="rod-panel-accent" style={{ background: "#4ade80" }} />
              <span className="rod-panel-title">Delivery Address</span>
            </div>
            <div className="rod-panel-body">
              <div className="rod-info-row">
                <span className="rod-info-label">Address</span>
                <span className="rod-info-value">
                  {getShippingAddressText(order?.shippingAddress, "No address recorded")}
                </span>
              </div>
              <ContactAction
                label="Customer"
                name={order?.customerName}
                phone={order?.shippingAddress?.customerNumber || order?.shippingAddress?.recipientPhone}
                compact
                style={{ marginBottom: 10 }}
              />
              {/* The same drop-off block as the admin order page: place
                  name, precision chip, Show map, Send pin, Copy pin. This
                  page printed the raw coordinates instead. */}
              {Number.isFinite(Number(order?.shippingAddress?.latLng?.lat)) &&
                Number.isFinite(Number(order?.shippingAddress?.latLng?.lng)) && (
                  <DropoffMap
                    lat={order.shippingAddress.latLng.lat}
                    lng={order.shippingAddress.latLng.lng}
                    address={getShippingAddressText(order?.shippingAddress, "")}
                    shippingAddress={order?.shippingAddress}
                    orderRef={order?.order_number || order?._id}
                    rider={order?.riderId}
                    country={order?.country}
                  />
                )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Row 2: Assignment + Status Control ── */}
      <div className="rod-main-grid">

        {/* Assignment Panel */}
        <div className="rod-panel">
          <div className="rod-panel-header">
            <div className="rod-panel-accent" style={{ background: "#fb923c" }} />
            <span className="rod-panel-title">Rider Assignment</span>
          </div>
          <div className="rod-panel-body">

            {/* Mode toggle */}
            {!order?.riderId && order?.delivery_status === "PROCESSING" && (
              <div className="rod-assignment-toggle">
                <button
                  className={`rod-toggle-btn ${assignmentMode === "rider" ? "active" : ""}`}
                  onClick={() => setAssignmentMode("rider")}
                >
                  <MdSportsMotorsports /> In-House Rider
                </button>
                <button
                  className={`rod-toggle-btn ${assignmentMode === "3pl" ? "active" : ""}`}
                  onClick={() => setAssignmentMode("3pl")}
                >
                  <MdLocalShipping /> 3PL Partner
                </button>
              </div>
            )}

            {/* Pending 3PL banner */}
            {order?.delivery_status === "PENDING_3PL" && (
              <div className="rod-3pl-alert" style={{ marginBottom: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                      <MdLocalShipping style={{ fontSize: 16 }} />
                      <strong style={{ fontSize: "0.82rem" }}>Pending 3PL Assignment</strong>
                    </div>
                    <div style={{ fontSize: "0.75rem", opacity: 0.9 }}>
                      Sent to: <strong>{order?.assignmentDetails?.partnerId?.companyName || "Unknown"}</strong>
                      <br />Waiting for 3PL to assign a rider.
                    </div>
                  </div>
                  <button
                    className="rod-btn rod-btn-danger"
                    onClick={() => setShowRecallModal(true)}
                    disabled={recalling3PL}
                  >
                    {recalling3PL
                      ? <><Spinner animation="border" size="sm" /> Recalling…</>
                      : <><MdUndo /> Recall</>
                    }
                  </button>
                </div>
              </div>
            )}

            {/* Rider dropdown — hidden for terminal (cancelled/rejected) orders */}
            {assignmentMode === "rider" &&
              order?.delivery_status !== "REJECTED" &&
              order?.delivery_status !== "CANCELLED" &&
              order?.delivery_status !== "CANCELED" && (
              <RiderSelectionDropdown
                order={order}
                riderId={riderId}
                setRiderId={setRiderId}
                loadingRiders={loadingRiders}
                riders={riders}
                assignRestaurantOrderToRider={assignRestaurantOrderToRider}
                elapsedAssignedTime={elapsedAssignedTime}
                elapsedDispatchedTime={elapsedDispatchedTime}
              />
            )}

            {/* 3PL dropdown + button */}
            {assignmentMode === "3pl" && order?.delivery_status === "PROCESSING" && (
              <>
                <ThreePLPartnerDropdown
                  dropoff={order?.shippingAddress?.latLng}
                  selectedPartnerId={selectedPartnerId}
                  onSelectPartner={setSelectedPartnerId}
                  disabled={false}
                />
                {selectedPartnerId && (
                  <button
                    className="rod-btn rod-btn-success"
                    style={{ marginTop: 10, width: "100%", justifyContent: "center" }}
                    onClick={assignOrderTo3PLPartner}
                    disabled={assigning3PL}
                  >
                    {assigning3PL
                      ? <><Spinner animation="border" size="sm" /> Assigning…</>
                      : <><MdLocalShipping /> Send to 3PL Partner</>
                    }
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Status Control Panel */}
        <div className="rod-panel">
          <div className="rod-panel-header">
            <div className="rod-panel-accent" style={{ background: "#a78bfa" }} />
            <span className="rod-panel-title">Order Controls</span>
          </div>
          <div className="rod-panel-body">

            {/* Status selector */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: "0.68rem", color: "#8888aa", textTransform: "uppercase", letterSpacing: "0.7px", fontWeight: 700, marginBottom: 6 }}>
                Update Status
              </div>
              <AppSelect
                value={order.delivery_status}
                onChange={(e) => handleUpdateStatus(e.target.value)}
                style={{
                  width: "100%",
                  background: "#1c1c2e",
                  border: "1px solid #3a3a58",
                  borderRadius: 4,
                  color: "#e0e0ff",
                  padding: "8px 10px",
                  fontSize: "0.82rem",
                  outline: "none",
                  cursor: "pointer",
                }}
              >
                <option value="PROCESSING" disabled={disabledStatusOptions.PROCESSING}>1. Processing</option>
                <option value="ASSIGNED"   disabled={disabledStatusOptions.ASSIGNED}>2. Assigned</option>
                <option value="DISPATCHED" disabled={disabledStatusOptions.DISPATCHED}>3. Dispatched</option>
                <option value="DELIVERED"  disabled={disabledStatusOptions.DELIVERED}>4. Delivered</option>
                <option value="REJECTED"   disabled={disabledStatusOptions.REJECTED}>5. Cancel Order</option>
              </AppSelect>
            </div>

{/* Admin voice: this reports what the vendor and rider are doing.
                See the AUDIENCE note in utilities/orderDeliverySteps.js. */}
            <OrderNextStep
              order={order}
              audience={AUDIENCE.ADMIN}
              style={{ marginBottom: "12px" }}
            />
            {/* Progress bar */}
            <div style={{ marginBottom: 18 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: "0.68rem", color: "#8888aa" }}>Progress</span>
                <span style={{ fontSize: "0.68rem", color: "#f2c811", fontWeight: 700 }}>{getProgress()}%</span>
              </div>
              <div className="rod-progress-bar-wrap">
                <div className="rod-progress-fill" style={{ width: `${getProgress()}%` }} />
              </div>
            </div>

            {/* Workflow actions */}
            <div style={{ fontSize: "0.68rem", color: "#8888aa", textTransform: "uppercase", letterSpacing: "0.7px", fontWeight: 700, marginBottom: 8 }}>
              Workflow Actions
            </div>
            <div className="rod-workflow-row">
              <button
                className="rod-btn rod-btn-warning"
                style={{ flex: 1, justifyContent: "center" }}
                onClick={handleDispatchRestaurantOrder}
                disabled={!canVendorDispatch(order)}
              >
                Dispatch Order
              </button>
              <button
                className="rod-btn rod-btn-success"
                style={{ flex: 1, justifyContent: "center" }}
                onClick={handleDeliverRestaurantOrder}
                disabled={!canVendorMarkDelivered(order)}
              >
                Mark Delivered
              </button>
            </div>

            {/* Mark as paid toggle */}
            <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #2a2a40", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: "0.78rem", color: "#ccccee" }}>
                {order?.isPaid ? "Order is Paid" : "Mark as Paid"}
              </span>
              <FormControlLabel
                control={
                  <IOSSwitch
                    checked={order?.isPaid}
                    onChange={handleMarkAsPaid}
                    color="primary"
                    disabled={order?.delivery_status !== "DELIVERED" || order?.isPaid}
                  />
                }
                label=""
                style={{ margin: 0 }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Cancel Modal ── */}
      <Modal show={showCancelModal} onHide={() => { setShowCancelModal(false); cancelReason.reset(); }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Cancellation</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to cancel this order?
          <CancelOrderFields state={cancelReason} isPaid={!!order?.isPaid} />
        </Modal.Body>
        <Modal.Footer>
          <button className="rod-btn rod-btn-ghost" onClick={() => setShowCancelModal(false)}>Close</button>
          <button className="rod-btn rod-btn-danger" onClick={handleCancelOrder}>Cancel Order</button>
        </Modal.Footer>
      </Modal>

      {/* ── Recall Modal ── */}
      <Modal show={showRecallModal} onHide={() => setShowRecallModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <MdUndo style={{ color: "#f87171" }} /> Recall Order
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ marginBottom: 10 }}>
            Recall this order from{" "}
            <strong style={{ color: "#e0e0ff" }}>{order?.assignmentDetails?.partnerId?.companyName || "the 3PL partner"}</strong>?
          </p>
          <div className="rod-3pl-alert" style={{ fontSize: "0.78rem" }}>
            <strong>Note:</strong> The order will be removed from the 3PL queue and returned to Processing.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="rod-btn rod-btn-ghost" onClick={() => setShowRecallModal(false)}>Cancel</button>
          <button className="rod-btn rod-btn-danger" onClick={recallOrderFrom3PL} disabled={recalling3PL}>
            {recalling3PL
              ? <><Spinner animation="border" size="sm" /> Recalling…</>
              : <><MdUndo /> Confirm Recall</>
            }
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default RestaurantOrderDetails;
